import { Separator } from "@/components/ui/separator";

export default function TermsPage() {
  return (
    <div data-testid="terms-page">
      {/* Hero */}
      <section className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <p className="font-marker text-teal text-sm">The fine print</p>
          <h1 className="mt-2 font-anton text-4xl tracking-wider text-sand stencil-text sm:text-6xl">
            TERMS OF SERVICE
          </h1>
          <div className="mt-2 h-1 w-20 bg-coral" />
        </div>
      </section>

      {/* Content */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="card-brutal bg-white p-8 lg:p-12">
            <p className="font-outfit text-sm text-ink/60">Last updated: February 2026</p>
            <p className="mt-4 font-courier text-sm text-ink/70 leading-relaxed">
              These Terms of Service apply to <strong>seasthebay.org</strong> (operated by Seas The Bay)
              and the HostBase guest portal at <strong>hostbase.app</strong>.
            </p>

            <Separator className="my-8 bg-ink/10" />

            <div className="space-y-8">
              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">1. ACCEPTANCE OF TERMS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  By accessing or using the seasthebay.org website or the HostBase guest portal ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service. The Service is a digital concierge platform that provides property information, guest services, and in-property shopping for short-term rental guests.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">2. ELIGIBILITY</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  You must be at least 18 years of age to use this Service. By using the Service, you represent and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms. Access to the Service is limited to registered guests with valid reservations as authorized by the property owner.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">3. ACCOUNT & AUTHENTICATION</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  Access to the Service requires verification via a one-time passcode (OTP) sent to your registered mobile phone number. You are responsible for:
                </p>
                <ul className="mt-3 space-y-1 pl-4 list-disc list-inside">
                  <li className="font-courier text-sm text-ink/70">Providing an accurate phone number</li>
                  <li className="font-courier text-sm text-ink/70">Maintaining the security of your authentication session</li>
                  <li className="font-courier text-sm text-ink/70">All activities that occur under your session</li>
                  <li className="font-courier text-sm text-ink/70">Notifying the property manager immediately if you suspect unauthorized access</li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">4. SERVICES & PRODUCTS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  The Service allows you to browse and purchase guest services (e.g., laundry, cleaning, transportation) and in-property products. By placing an order:
                </p>
                <ul className="mt-3 space-y-1 pl-4 list-disc list-inside">
                  <li className="font-courier text-sm text-ink/70">You agree to pay the listed price plus any applicable taxes or fees</li>
                  <li className="font-courier text-sm text-ink/70">Service availability and fulfillment times are subject to property manager availability</li>
                  <li className="font-courier text-sm text-ink/70">Product availability is subject to current inventory and may be limited</li>
                  <li className="font-courier text-sm text-ink/70">Prices are listed in US Dollars and are subject to change without notice</li>
                  <li className="font-courier text-sm text-ink/70">The property owner (Seas The Bay) reserves the right to refuse or cancel any order</li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">5. PAYMENTS & REFUNDS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  All payments are processed securely through Stripe. By making a purchase:
                </p>
                <ul className="mt-3 space-y-1 pl-4 list-disc list-inside">
                  <li className="font-courier text-sm text-ink/70">You authorize the charge to your selected payment method</li>
                  <li className="font-courier text-sm text-ink/70">All sales are final unless otherwise specified by the property manager</li>
                  <li className="font-courier text-sm text-ink/70">Refund requests must be directed to the property manager within 24 hours of purchase</li>
                  <li className="font-courier text-sm text-ink/70">Refunds, if approved, will be processed to the original payment method within 5-10 business days</li>
                  <li className="font-courier text-sm text-ink/70">Seas The Bay reserves the right to approve or deny refund requests at their discretion</li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">6. PROPERTY RULES & GUEST CONDUCT</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  As a guest at Seas The Bay, you agree to:
                </p>
                <ul className="mt-3 space-y-1 pl-4 list-disc list-inside">
                  <li className="font-courier text-sm text-ink/70">Follow all property rules and guidelines provided through the Service</li>
                  <li className="font-courier text-sm text-ink/70">Treat the property, its contents, and surrounding areas with respect</li>
                  <li className="font-courier text-sm text-ink/70">Not use the Service for any unlawful purpose</li>
                  <li className="font-courier text-sm text-ink/70">Not attempt to access areas of the Service not intended for your use</li>
                  <li className="font-courier text-sm text-ink/70">Report any damage, maintenance issues, or safety concerns promptly</li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">7. SMS COMMUNICATIONS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  By using the Service, you consent to receive SMS messages including authentication codes, order confirmations, and property-related notifications. Message and data rates may apply based on your mobile carrier plan. Message frequency varies based on your usage. You may opt out of non-essential messages by contacting the property manager at seasthebay.org.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">8. INTELLECTUAL PROPERTY</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are the property of HostBase and Seas The Bay or their licensors and are protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from any content without prior written consent.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">9. LIMITATION OF LIABILITY</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed uppercase">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, HOSTBASE, SEAS THE BAY, AND THE PROPERTY OWNER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID THROUGH THE SERVICE IN THE 30 DAYS PRECEDING THE CLAIM.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">10. DISCLAIMER OF WARRANTIES</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed uppercase">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">11. INDEMNIFICATION</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless HostBase, Seas The Bay, the property owner, and their respective officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your use of the Service or violation of these Terms.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">12. MODIFICATIONS TO TERMS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  We reserve the right to modify these Terms at any time. Changes will be effective upon posting to the Service. Your continued use of the Service after changes are posted constitutes your acceptance of the modified Terms. We encourage you to review these Terms periodically.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">13. GOVERNING LAW</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved in the courts of Volusia County, Florida.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">14. CONTACT</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  For questions regarding these Terms of Service, please contact the property manager through the guest portal at{" "}
                  <a href="https://hostbase.app" target="_blank" rel="noopener noreferrer" className="text-teal underline">hostbase.app</a>{" "}
                  or visit us at{" "}
                  <a href="https://seasthebay.org" target="_blank" rel="noopener noreferrer" className="text-teal underline">seasthebay.org</a>{" "}
                  or email us at stay@seasthebay.org.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
